import { useCallback, useEffect, useState, type ReactNode } from 'react';

import { api } from '../api';
import { Aviso, Etiqueta, Painel, data, useAcao, type TomEtiqueta } from '../comum';

/**
 * Fila de ações que tocam o parque — não de sugestões de IA.
 *
 * 🔴 **Esta tela existe para não deixar `REQUEST_ACCEPTED` parecer sucesso.**
 *
 * O KACE responde que *recebeu* o pedido de inventário. Se a máquina estava
 * desligada, ela não coletou nada. Mostrar "Executado ✓" nesse momento seria a
 * tela mentindo com todos os dados na mão: quem opera fecharia o chamado, e a
 * coleta nunca teria acontecido. Por isso a execução e a verificação aparecem
 * como duas linhas distintas, e a segunda é a que responde "deu certo?".
 */

export interface AprovacaoDeAcao {
  readonly id: string;
  readonly actionId: string;
  readonly status: 'PENDING' | 'APPROVED' | 'REJECTED' | 'EXPIRED';
  readonly tool: string;
  readonly resource: { readonly type: string; readonly id: number | string };
  /** Entrada da ferramenta. Para script, traz o `scriptId` — nunca código. */
  readonly input?: unknown;
  readonly requestedAt: string;
  readonly expiresAt: string;
  readonly decidedBy?: string;
  readonly decisionReason?: string;
  readonly executedAt?: string;
}

interface Verificacao {
  readonly id: string;
  readonly status: 'PENDING' | 'SUCCESS' | 'FAILED' | 'UNKNOWN' | 'TIMEOUT';
  readonly strategy: string;
  readonly attempts: number;
  readonly timeoutAt: string | null;
  readonly completedAt: string | null;
  readonly evidence: Record<string, unknown> | null;
}

interface Execucao {
  readonly actionId: string;
  readonly machineId: number | null;
  readonly status: string;
  readonly requestedAt: string;
  readonly verification: Verificacao | null;
}

const ROTULO_TOOL: Readonly<Record<string, string>> = {
  requestForceInventory: 'Forçar inventário',
  requestScriptRun: 'Executar script homologado',
};

/**
 * O que está sendo aprovado, em uma linha.
 *
 * 🔴 Para script, o número do script aparece junto. "Executar script homologado
 * na máquina 42" não diz **qual** — e quem aprova sem saber o que vai rodar não
 * está decidindo, está clicando.
 */
function alvoDa(a: AprovacaoDeAcao): string {
  const maquina = `máquina ${a.resource.id}`;
  const script = (a.input as { scriptId?: number } | undefined)?.scriptId;
  return script === undefined ? maquina : `script #${script} · ${maquina}`;
}

const APROVACAO: Readonly<Record<string, { rotulo: string; tom: TomEtiqueta }>> = {
  PENDING: { rotulo: 'Aguardando decisão', tom: 'alerta' },
  APPROVED: { rotulo: 'Aprovada', tom: 'ok' },
  REJECTED: { rotulo: 'Rejeitada', tom: 'erro' },
  EXPIRED: { rotulo: 'Expirada', tom: 'neutro' },
};

/**
 * Os quatro desfechos, em português e sem eufemismo.
 *
 * `UNKNOWN` não é "provavelmente deu certo": é o produto dizendo que não
 * conseguiu provar nada. Quem lê precisa saber que ainda tem trabalho ali.
 */
const VERIFICACAO: Readonly<
  Record<string, { rotulo: string; tom: TomEtiqueta; explica: string }>
> = {
  PENDING: {
    rotulo: 'Verificando…',
    tom: 'alerta',
    explica: 'O pedido foi aceito. Ainda não há evidência de que a coleta aconteceu.',
  },
  SUCCESS: {
    rotulo: 'Confirmado',
    tom: 'ok',
    explica: 'O timestamp de inventário avançou depois do pedido — a coleta aconteceu.',
  },
  FAILED: {
    rotulo: 'Não coletou',
    tom: 'erro',
    explica: 'O timestamp de inventário não avançou. A máquina não coletou.',
  },
  UNKNOWN: {
    rotulo: 'Sem evidência',
    tom: 'neutro',
    explica:
      'Não foi possível provar nem descartar a coleta. Não trate como sucesso: verifique a máquina.',
  },
  TIMEOUT: {
    rotulo: 'Prazo esgotado',
    tom: 'erro',
    explica: 'A janela de espera acabou sem evidência de coleta.',
  },
};

/** Estado que a tela não conhece não vira "ok" por omissão. */
const DESFECHO_DESCONHECIDO = {
  rotulo: 'Estado não reconhecido',
  tom: 'neutro' as TomEtiqueta,
  explica: 'O servidor devolveu um estado que esta versão da tela não conhece. Não presuma sucesso.',
};

function EstadoDaExecucao({ execucao }: { execucao: Execucao }): ReactNode {
  const v = execucao.verification;
  const desfecho =
    (v === null ? VERIFICACAO['PENDING'] : VERIFICACAO[v.status]) ?? DESFECHO_DESCONHECIDO;
  return (
    <div className="proposta" style={{ marginTop: 8 }}>
      <div className="proposta-campo">
        <span className="proposta-rotulo">Pedido ao KACE</span>
        <span className="proposta-valor">
          <Etiqueta tom="neutro" titulo="O appliance recebeu o pedido. Não é confirmação de coleta.">
            {execucao.status === 'REQUEST_ACCEPTED' ? 'Aceito pelo KACE' : execucao.status}
          </Etiqueta>{' '}
          <span className="suave">· {data(execucao.requestedAt)}</span>
        </span>
      </div>
      <div className="proposta-campo">
        <span className="proposta-rotulo">Verificação</span>
        <span className="proposta-valor">
          <Etiqueta tom={desfecho.tom} titulo={desfecho.explica}>
            {desfecho.rotulo}
          </Etiqueta>
          {v !== null && v.attempts > 0 && (
            <span className="suave"> · {v.attempts} tentativa(s)</span>
          )}
        </span>
      </div>
      <p className="suave">{desfecho.explica}</p>
      {v?.evidence != null && (
        <details>
          <summary className="suave">Evidência</summary>
          <pre className="mono">{JSON.stringify(v.evidence, null, 2)}</pre>
        </details>
      )}
      <p className="suave mono" style={{ fontSize: '0.8em' }}>
        action {execucao.actionId}
        {v !== null && ` · verification ${v.id}`}
        {execucao.machineId !== null && ` · máquina ${execucao.machineId}`}
      </p>
    </div>
  );
}

export function AcoesOperacionais({
  aoPerderSessao,
  podeDecidir,
}: {
  aoPerderSessao: () => void;
  podeDecidir: boolean;
}): ReactNode {
  const acao = useAcao(aoPerderSessao);
  const [itens, setItens] = useState<readonly AprovacaoDeAcao[] | null>(null);
  const [execucoes, setExecucoes] = useState<Record<string, Execucao | null>>({});
  const [motivo, setMotivo] = useState<Record<string, string>>({});

  const carregar = useCallback(async (): Promise<void> => {
    try {
      const r = await api.get<{ itens: readonly AprovacaoDeAcao[] }>('/api/actions/approvals');
      setItens(r.itens);
      // A execução de cada aprovação já consumida vem junto: sem isso a linha
      // ficaria em "Aprovada" para sempre, sem dizer o que aconteceu depois.
      const consumidas = r.itens.filter((a) => a.executedAt !== undefined);
      const detalhes = await Promise.all(
        consumidas.map(async (a) => {
          const d = await api.get<{ execution: Execucao | null }>(
            `/api/actions/approvals/${encodeURIComponent(a.id)}`,
          );
          return [a.id, d.execution] as const;
        }),
      );
      setExecucoes(Object.fromEntries(detalhes));
    } catch (e: unknown) {
      if (e instanceof Error && e.message.includes('401')) aoPerderSessao();
      else setItens([]);
    }
  }, [aoPerderSessao]);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  /**
   * Enquanto houver verificação em aberto, a tela volta a perguntar.
   *
   * O desfecho chega por um worker que roda no servidor, não por esta aba.
   * Sem o recarregamento, quem executou ficaria olhando "Verificando…" até
   * apertar F5 — e concluiria pelo silêncio que travou.
   */
  const emAberto = Object.values(execucoes).some(
    (e) => e !== null && (e.verification === null || e.verification.status === 'PENDING'),
  );
  useEffect(() => {
    if (!emAberto) return;
    const t = setInterval(() => void carregar(), 20_000);
    return () => clearInterval(t);
  }, [emAberto, carregar]);

  const decidir = (a: AprovacaoDeAcao, outcome: 'APPROVED' | 'REJECTED'): void => {
    const razao = (motivo[a.id] ?? '').trim();
    if (outcome === 'REJECTED' && razao === '') {
      acao.falhar('Rejeitar exige motivo — é ele que explica a decisão na auditoria.');
      return;
    }
    void acao.rodar(async () => {
      await api.post(`/api/actions/approvals/${encodeURIComponent(a.id)}/decidir`, {
        outcome,
        ...(razao === '' ? {} : { reason: razao }),
      });
      await carregar();
      return outcome === 'APPROVED' ? 'Ação aprovada.' : 'Ação rejeitada.';
    });
  };

  const executar = (a: AprovacaoDeAcao): void => {
    void acao.rodar(async () => {
      await api.post(`/api/actions/approvals/${encodeURIComponent(a.id)}/executar`);
      await carregar();
      // A frase é deliberada: o pedido foi enviado, o resultado ainda não existe.
      return 'Pedido enviado ao KACE. A verificação dirá se a coleta aconteceu.';
    });
  };

  const titulo =
    itens === null || itens.length === 0 ? 'Ações no parque' : `Ações no parque — ${itens.length}`;

  return (
    <Painel titulo={titulo}>
      {acao.erro !== null && <Aviso tipo="erro">{acao.erro}</Aviso>}
      {acao.ok !== null && <Aviso tipo="ok">{acao.ok}</Aviso>}

      {itens !== null && itens.length === 0 && (
        <p className="suave">Nenhuma ação solicitada.</p>
      )}

      {(itens ?? []).map((a) => {
        const estado = APROVACAO[a.status] ?? { rotulo: a.status, tom: 'neutro' as TomEtiqueta };
        const execucao = execucoes[a.id] ?? null;
        return (
          <div
            key={a.id}
            style={{ borderBottom: '1px solid var(--borda)', paddingBottom: 16, marginBottom: 16 }}
          >
            <div className="linha" style={{ marginBottom: 8 }}>
              <div>
                <strong>{ROTULO_TOOL[a.tool] ?? a.tool}</strong>{' '}
                <span className="suave">
                  · {alvoDa(a)} · solicitada {data(a.requestedAt)}
                  {a.status === 'PENDING' && ` · expira ${data(a.expiresAt)}`}
                </span>
              </div>
              <Etiqueta tom={estado.tom}>{estado.rotulo}</Etiqueta>
            </div>

            {a.decidedBy !== undefined && (
              <p className="suave">
                Decidida por {a.decidedBy}
                {a.decisionReason !== undefined && ` — ${a.decisionReason}`}
              </p>
            )}

            {execucao !== null && <EstadoDaExecucao execucao={execucao} />}

            {a.status === 'PENDING' && podeDecidir && (
              <div className="linha" style={{ marginTop: 12 }}>
                <input
                  placeholder="Motivo (obrigatório para rejeitar)"
                  value={motivo[a.id] ?? ''}
                  onChange={(e) => setMotivo({ ...motivo, [a.id]: e.target.value })}
                />
                <button
                  className="primario encolhe"
                  disabled={acao.executando}
                  onClick={() => decidir(a, 'APPROVED')}
                >
                  Aprovar
                </button>
                <button
                  className="perigo encolhe"
                  disabled={acao.executando}
                  onClick={() => decidir(a, 'REJECTED')}
                >
                  Rejeitar
                </button>
              </div>
            )}

            {a.status === 'APPROVED' && a.executedAt === undefined && (
              <div className="linha" style={{ marginTop: 12 }}>
                <span className="suave">
                  Aprovada e ainda não executada. Executar envia o pedido ao KACE.
                </span>
                <button
                  className="primario encolhe"
                  disabled={acao.executando}
                  onClick={() => executar(a)}
                >
                  Executar
                </button>
              </div>
            )}
          </div>
        );
      })}
    </Painel>
  );
}
